import { Badge, Box, Heading, HStack, Skeleton, VStack } from '@chakra-ui/react';
import {
  LIVE_MENTION_BOOST_DELIVERY_STATES,
  MentionBoostDeliveryState,
  type MentionBoostInsightsDto,
} from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import { useMentionBoostInsightsQuery } from '@/hooks/queries/use-mention-boost-insights-query';
import { BoostDeliveryStatus, DELIVERY_TONE, leadsWithReason } from './BoostDeliveryStatus';
import { BoostInsightsSummaryCard } from './boost-insights/BoostInsightsSummaryCard';
import { BoostInsightsMetricGrid } from './boost-insights/BoostInsightsMetricGrid';
import { BoostInsightsTrendChart } from './boost-insights/BoostInsightsTrendChart';
import {
  AttributionLabel,
  ConsolidatingNotice,
  NoDataState,
} from './boost-insights/BoostInsightsNotices';
import {
  formatLastSynced,
  hasNoData,
  isConsolidating,
  resolveInsightsView,
} from './boost-insights/boost-insights-format';

const LIVE_STATES = new Set<string>(LIVE_MENTION_BOOST_DELIVERY_STATES);

interface BoostStatusInlineProps {
  mentionId: string;
  /** Delivery state of the launched boost (any non-draft state). */
  deliveryState: MentionBoostDeliveryState;
  /** Last provider error code, surfaced as the reason card for failed / needs-attention. */
  lastErrorCode?: string | null;
}

/**
 * The single inline view of a launched boost, shared by every delivery state.
 * It leads with a compact status row (title + honest delivery badge), surfaces
 * the repair reason for failed / needs-attention, and renders the Meta-modeled
 * paid performance inline — the objective-adaptive summary plus the full metric
 * grid — so the marketer never needs a second click to read the figures. A
 * `draft` boost is owned by the setup view and renders nothing here.
 */
export function BoostStatusInline({
  mentionId,
  deliveryState,
  lastErrorCode,
}: BoostStatusInlineProps) {
  const { t, i18n } = useTranslation();
  const { data: insights, isLoading } = useMentionBoostInsightsQuery({ mentionId });

  if (deliveryState === MentionBoostDeliveryState.DRAFT) {
    return null;
  }

  const tone = DELIVERY_TONE[deliveryState];
  const isReasonBearing = leadsWithReason(deliveryState, lastErrorCode);
  // A failed boost never delivered, so its reason card is the whole body. Every
  // other state can carry figures (live now, stopped historically).
  const isFailed = deliveryState === MentionBoostDeliveryState.FAILED;

  return (
    <Box py={5}>
      <HStack justify="space-between" mb={3} gap={3}>
        <Heading size="sm" color="gray.900">
          {t('mentions.boost.title')}
        </Heading>
        <Badge colorPalette={tone} variant="subtle" textTransform="none" flexShrink={0}>
          {t(`mentions.boost.delivery.${deliveryState}.label`)}
        </Badge>
      </HStack>

      {isReasonBearing && (
        <Box mb={isFailed ? 0 : 4}>
          <BoostDeliveryStatus
            deliveryState={deliveryState}
            lastErrorCode={lastErrorCode}
            hideHeader
          />
        </Box>
      )}

      {!isFailed && (
        <BoostStatusBody
          deliveryState={deliveryState}
          lastErrorCode={lastErrorCode}
          insights={insights}
          isLoading={isLoading}
          locale={i18n.language}
          reasonAlreadyShown={isReasonBearing}
        />
      )}
    </Box>
  );
}

interface BoostStatusBodyProps {
  deliveryState: MentionBoostDeliveryState;
  lastErrorCode?: string | null;
  insights: MentionBoostInsightsDto | undefined;
  isLoading: boolean;
  locale: string;
  /** The reason card is already at the top; the tail must not repeat it. */
  reasonAlreadyShown: boolean;
}

/**
 * The paid-performance body for any non-failed launched boost. The two
 * transition states (publishing / stopping) lead with their tone-tinted copy
 * card so the marketer knows what Meta is doing right now; publishing pairs it
 * with the clean "no performance yet" block. Otherwise: the figures when data
 * exists, a clean empty state while a live boost is still producing them, and
 * the settled-status copy line for a stopped boost that never delivered.
 */
function BoostStatusBody({
  deliveryState,
  lastErrorCode,
  insights,
  isLoading,
  locale,
  reasonAlreadyShown,
}: BoostStatusBodyProps) {
  if (isLoading) {
    return <Skeleton h="120px" rounded="lg" data-testid="boost-status-summary-loading" />;
  }

  const isTransitioning =
    deliveryState === MentionBoostDeliveryState.PUBLISHING ||
    deliveryState === MentionBoostDeliveryState.STOPPING;
  const transitionCopyCard = isTransitioning ? (
    <BoostDeliveryStatus deliveryState={deliveryState} lastErrorCode={lastErrorCode} hideHeader />
  ) : null;

  if (insights && !hasNoData(insights)) {
    const view = resolveInsightsView(insights);
    return (
      <VStack align="stretch" gap={4}>
        {transitionCopyCard}
        {isConsolidating(insights) && <ConsolidatingNotice />}
        <BoostInsightsSummaryCard insights={insights} view={view} locale={locale} />
        <BoostInsightsTrendChart insights={insights} view={view} />
        <BoostInsightsMetricGrid insights={insights} view={view} locale={locale} />
        <AttributionLabel lastSyncedLabel={formatLastSynced(insights.lastSyncedAt, locale)} />
      </VStack>
    );
  }

  if (deliveryState === MentionBoostDeliveryState.PUBLISHING) {
    return (
      <VStack align="stretch" gap={3}>
        {transitionCopyCard}
        <NoDataState />
      </VStack>
    );
  }

  // A stopping boost with no figures is a pure transition: its copy card is
  // the whole story, an empty "no data" block would only add noise.
  if (deliveryState === MentionBoostDeliveryState.STOPPING) {
    return transitionCopyCard;
  }

  // A live boost is still producing its first figures; a settled (stopped) one
  // that never delivered shows its honest status line instead of an empty grid.
  if (LIVE_STATES.has(deliveryState)) {
    return (
      <VStack align="stretch" gap={4}>
        <NoDataState />
        <AttributionLabel lastSyncedLabel={null} />
      </VStack>
    );
  }

  // A stop that named its cause already said so above. Repeating the same card
  // under the figures would read like two separate statements about one stop.
  if (reasonAlreadyShown) {
    return null;
  }

  return (
    <BoostDeliveryStatus deliveryState={deliveryState} lastErrorCode={lastErrorCode} hideHeader />
  );
}
